import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Calendar, Trash2, X } from 'lucide-react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function SchedulesPage() {
  const [schedules, setSchedules] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [devices, setDevices] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: '', device_id: '', group_id: '', playlist_id: '',
    start_time: '08:00', end_time: '18:00',
    days_of_week: '0,1,2,3,4,5,6', priority: 0,
  });

  const fetchAll = async () => {
    try {
      const [schRes, plRes, devRes, grRes] = await Promise.all([
        api.get('/schedules'), api.get('/playlists'),
        api.get('/devices'), api.get('/device-groups'),
      ]);
      setSchedules(schRes.data.schedules || []);
      setPlaylists(plRes.data.playlists || []);
      setDevices(devRes.data.devices || []);
      setGroups(grRes.data.groups || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/schedules', {
        ...form,
        device_id: form.device_id || null,
        group_id: form.group_id || null,
      });
      toast.success('Schedule created');
      setShowCreate(false);
      setForm({ name: '', device_id: '', group_id: '', playlist_id: '', start_time: '08:00', end_time: '18:00', days_of_week: '0,1,2,3,4,5,6', priority: 0 });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await api.delete(`/schedules/${id}`);
      toast.success('Deleted');
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  const toggleDay = (dayIndex) => {
    const current = form.days_of_week.split(',').filter(Boolean);
    const str = String(dayIndex);
    const updated = current.includes(str) ? current.filter(d => d !== str) : [...current, str];
    setForm({ ...form, days_of_week: updated.sort().join(',') });
  };

  if (loading) return <div className="loading-page"><span className="spinner" /></div>;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h2>Schedules</h2>
          <div className="page-header-sub">Time-based playlist assignments</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} /> New Schedule
        </button>
      </div>

      {schedules.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Calendar size={48} />
            <h3>No schedules yet</h3>
            <p>Create schedules to automatically change playlists based on time of day.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Target</th>
                  <th>Playlist</th>
                  <th>Time</th>
                  <th>Days</th>
                  <th>Priority</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map(s => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 500 }}>{s.name || '—'}</td>
                    <td style={{ fontSize: 13 }}>
                      {s.device_tv_number ? `TV: ${s.device_tv_number}` :
                       s.group_name ? `Group: ${s.group_name}` : '—'}
                    </td>
                    <td>
                      <span className="badge badge-info">{s.playlist_name}</span>
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                      {s.start_time?.slice(0, 5)} — {s.end_time?.slice(0, 5)}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {s.days_of_week?.split(',').map(d => DAYS[parseInt(d)]).join(', ')}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{s.priority}</td>
                    <td>
                      <button className="btn btn-ghost btn-icon btn-sm"
                        style={{ color: 'var(--danger)' }}
                        onClick={() => handleDelete(s.id)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Schedule</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowCreate(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Schedule Name</label>
                  <input className="form-input" value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Morning Classes" />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Assign to Device</label>
                    <select className="form-input" value={form.device_id}
                      onChange={e => setForm({ ...form, device_id: e.target.value, group_id: '' })}>
                      <option value="">— None —</option>
                      {devices.map(d => <option key={d.id} value={d.id}>{d.tv_number || d.name || d.device_uid?.slice(0,8)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Or Group</label>
                    <select className="form-input" value={form.group_id}
                      onChange={e => setForm({ ...form, group_id: e.target.value, device_id: '' })}>
                      <option value="">— None —</option>
                      {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Playlist</label>
                  <select className="form-input" value={form.playlist_id}
                    onChange={e => setForm({ ...form, playlist_id: e.target.value })} required>
                    <option value="">Select playlist...</option>
                    {playlists.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Start Time</label>
                    <input className="form-input" type="time" value={form.start_time}
                      onChange={e => setForm({ ...form, start_time: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">End Time</label>
                    <input className="form-input" type="time" value={form.end_time}
                      onChange={e => setForm({ ...form, end_time: e.target.value })} required />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Days</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {DAYS.map((day, i) => (
                      <button key={i} type="button"
                        className={`btn btn-sm ${form.days_of_week.split(',').includes(String(i)) ? 'btn-primary' : 'btn-secondary'}`}
                        style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={() => toggleDay(i)}>
                        {day}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Priority (higher = takes precedence)</label>
                  <input className="form-input" type="number" min="0" max="100"
                    value={form.priority}
                    onChange={e => setForm({ ...form, priority: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create Schedule</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
